// Loan Processing Modal - Bank Eligibility Check with CIBIL + Bank Statement + Bank Master
import React, { useState, useEffect } from 'react';
import { loansApi } from '@/services/api';
import { toast } from 'sonner';
import {
  Car, CreditCard, CheckCircle, XCircle, Building2, Plus,
  IndianRupee, RefreshCw, Loader2, AlertTriangle, TrendingUp,
  Wallet, FileText, MapPin, Shield, ChevronDown, ChevronUp, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { formatCurrency } from './utils';
import { AppStatusBadge } from './StatusBadges';
import BankOffersModal from './BankOffersModal';

const DataChip = ({ icon: Icon, label, value, status }) => {
  const colors = {
    good: 'bg-green-50 text-green-700 border-green-200',
    warn: 'bg-amber-50 text-amber-700 border-amber-200',
    bad: 'bg-red-50 text-red-700 border-red-200',
    neutral: 'bg-slate-50 text-slate-600 border-slate-200',
    missing: 'bg-gray-50 text-gray-400 border-gray-200 border-dashed',
  };
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${colors[status || 'neutral']}`}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
        <p className="font-semibold truncate">{value || 'Not Available'}</p>
      </div>
    </div>
  );
};

const LoanProcessingModal = ({ isOpen, onClose, lead, onUpdate }) => {
  const [checking, setChecking] = useState(null);
  const [vehicleData, setVehicleData] = useState({});
  const [applying, setApplying] = useState(null);
  const [bankOffersModal, setBankOffersModal] = useState({ open: false, vehicle: null, application: null });
  const [expandedRejects, setExpandedRejects] = useState({});
  
  const vehicles = lead?.vehicles || [];
  const applications = lead?.applications || [];
  const loanOffers = lead?.loan_offers || [];
  
  const handleCheckEligibility = async (vehicleId) => {
    const vehicle = vehicles.find(v => v.vehicle_id === vehicleId);
    if (!vehicle?.vehicle_valuation) {
      toast.error('Please set vehicle valuation first');
      return;
    }
    
    setChecking(vehicleId);
    try {
      const res = await loansApi.checkEligibility(lead.id, vehicleId);
      setVehicleData(prev => ({
        ...prev,
        [vehicleId]: {
          results: res.data.results || [],
          summary: res.data.customer_summary || {},
          eligible: res.data.eligible_banks || 0,
          total: res.data.total_banks || 0,
        }
      }));
      const eligible = res.data.eligible_banks || 0;
      const total = res.data.total_banks || 0;
      toast.success(`${eligible} of ${total} banks eligible`);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check eligibility');
    } finally {
      setChecking(null);
    }
  };
  
  const handleApplyLoan = async (vehicleId, bankId) => {
    setApplying(`${vehicleId}-${bankId}`);
    try {
      await loansApi.createApplication(lead.id, {
        vehicle_loan_id: vehicleId,
        bank_id: bankId
      });
      toast.success('Loan application submitted');
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplying(null);
    }
  };
  
  const hasAppliedToBank = (vehicleId, bankId) => {
    return applications.some(a => a.vehicle_loan_id === vehicleId && a.bank_id === bankId);
  };
  
  const getScoreStatus = (score) => {
    if (!score) return 'missing';
    if (score >= 750) return 'good';
    if (score >= 650) return 'warn';
    return 'bad';
  };
  
  const toggleRejectExpand = (key) => {
    setExpandedRejects(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto p-0" data-testid="loan-processing-modal">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-5 w-5 text-blue-600" />
            Bank Eligibility Check
          </DialogTitle>
          <DialogDescription>
            {lead?.customer_name} &mdash; Cross-referencing CIBIL, Bank Statement & Bank Master rules
          </DialogDescription>
        </DialogHeader>
        
        <div className="px-6 pb-6 space-y-5">
          {vehicles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Car className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">No vehicles added</p>
              <p className="text-sm">Please add vehicles first to check loan eligibility</p>
            </div>
          ) : (
            vehicles.map((vehicle, idx) => {
              const vData = vehicleData[vehicle.vehicle_id] || {};
              const results = vData.results || [];
              const summary = vData.summary || {};
              const eligibleResults = results.filter(r => r.is_eligible);
              const rejectedResults = results.filter(r => !r.is_eligible);
              const vehicleApps = applications.filter(a => a.vehicle_loan_id === vehicle.vehicle_id);
              const vehicleOffers = loanOffers.filter(o => o.vehicle_loan_id === vehicle.vehicle_id);
              
              return (
                <div key={vehicle.vehicle_id} className="border rounded-xl overflow-hidden bg-white shadow-sm">
                  {/* Vehicle Header */}
                  <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                          <Car className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{vehicle.car_number}</h3>
                          <p className="text-sm text-slate-300">
                            {vehicle.car_make} {vehicle.car_model} {vehicle.car_year}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Valuation</p>
                        <p className="text-xl font-bold">{formatCurrency(vehicle.vehicle_valuation)}</p>
                      </div>
                    </div>
                    
                    <div className="mt-3 grid grid-cols-4 gap-3">
                      {[
                        { label: 'Loan Required', val: formatCurrency(vehicle.required_loan_amount) },
                        { label: 'Expected EMI', val: formatCurrency(vehicle.expected_emi) },
                        { label: 'Interest Rate', val: vehicle.expected_interest_rate ? `${vehicle.expected_interest_rate}%` : '-' },
                        { label: 'Tenure', val: vehicle.expected_tenure_months ? `${vehicle.expected_tenure_months} mo` : '-' },
                      ].map(item => (
                        <div key={item.label} className="bg-white/10 rounded-lg px-3 py-2 text-center">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wide">{item.label}</p>
                          <p className="font-semibold text-sm">{item.val || '-'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Customer Data Summary - shows after check */}
                  {results.length > 0 && (
                    <div className="px-4 py-3 bg-slate-50 border-b">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <Info className="h-3.5 w-3.5" /> Customer Data Used for Eligibility
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                        <DataChip icon={CreditCard} label="CIBIL Score" value={summary.credit_score} status={getScoreStatus(summary.credit_score)} />
                        <DataChip icon={IndianRupee} label="Monthly Income" value={summary.monthly_income ? formatCurrency(summary.monthly_income) : null} status={summary.monthly_income ? (summary.monthly_income >= 25000 ? 'good' : 'warn') : 'missing'} />
                        <DataChip icon={Wallet} label="Avg Bank Balance" value={summary.avg_bank_balance ? formatCurrency(summary.avg_bank_balance) : null} status={summary.avg_bank_balance ? 'neutral' : 'missing'} />
                        <DataChip icon={FileText} label="Employment" value={summary.employment_type} status="neutral" />
                        <DataChip icon={AlertTriangle} label="Bounces" value={summary.bounce_count?.toString() || '0'} status={summary.bounce_count > 0 ? 'bad' : 'good'} />
                        <DataChip icon={MapPin} label="Location" value={summary.location_type} status="neutral" />
                      </div>
                      {(!summary.has_credit_report || !summary.has_bank_statement) && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-md border border-amber-200">
                          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>
                            {!summary.has_credit_report && !summary.has_bank_statement
                              ? 'Credit report & bank statement not available. Results may be inaccurate.'
                              : !summary.has_credit_report
                              ? 'Credit report not available. Some banks may reject without score.'
                              : 'Bank statement not analyzed. Income-based checks will use defaults.'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Check Eligibility Button */}
                  <div className="p-4 border-b flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {results.length > 0
                          ? <span className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle className="h-4 w-4" /> {eligibleResults.length} Approved</span>
                              <span className="text-gray-300">|</span>
                              <span className="inline-flex items-center gap-1 text-red-500"><XCircle className="h-4 w-4" /> {rejectedResults.length} Rejected</span>
                              <span className="text-gray-300">|</span>
                              <span className="text-gray-500">{results.length} Total Banks</span>
                            </span>
                          : 'Check eligibility against all partner banks'}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleCheckEligibility(vehicle.vehicle_id)}
                      disabled={checking === vehicle.vehicle_id || !vehicle.vehicle_valuation}
                      data-testid="check-eligibility-btn"
                    >
                      {checking === vehicle.vehicle_id ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      {results.length > 0 ? 'Re-check' : 'Check Eligibility'}
                    </Button>
                  </div>
                  
                  {/* Active Applications */}
                  {vehicleApps.length > 0 && (
                    <div className="px-4 py-3 border-b bg-blue-50/50">
                      <p className="text-xs font-medium text-gray-600 mb-2">Active Applications</p>
                      <div className="flex flex-wrap gap-2">
                        {vehicleApps.map((app) => {
                          const appOffers = loanOffers.filter(o => o.application_id === app.id);
                          const hasOffer = appOffers.length > 0;
                          const acceptedOffer = appOffers.find(o => o.offer_status === 'ACCEPTED');
                          return (
                            <div key={app.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border text-sm">
                              <Building2 className="h-3.5 w-3.5 text-gray-400" />
                              <span className="font-medium">{app.bank_name}</span>
                              <AppStatusBadge status={app.status} />
                              {(app.status === 'OFFER_RECEIVED' || app.status === 'APPROVED_BY_BANK' || app.status === 'IN_PROCESS') && (
                                <Button size="sm" variant={hasOffer ? "outline" : "default"} className="h-5 text-[10px] ml-1 px-2"
                                  onClick={() => setBankOffersModal({ open: true, vehicle, application: app })}>
                                  {acceptedOffer ? <><CheckCircle className="h-3 w-3 mr-0.5 text-green-600" /> Accepted</> : hasOffer ? `Offers (${appOffers.length})` : <><Plus className="h-3 w-3 mr-0.5" /> Offer</>}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Bank Offers Summary */}
                  {vehicleOffers.length > 0 && (
                    <div className="px-4 py-3 border-b bg-green-50/50">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-600 flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5 text-green-600" /> Bank Offers ({vehicleOffers.length})</p>
                        <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setBankOffersModal({ open: true, vehicle, application: null })}>View All</Button>
                      </div>
                      {vehicleOffers.find(o => o.offer_status === 'ACCEPTED') && (
                        <div className="p-2.5 rounded-lg bg-green-100 border border-green-300 flex items-center justify-between">
                          <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" /><span className="font-medium text-sm text-green-800">Accepted: {vehicleOffers.find(o => o.offer_status === 'ACCEPTED').bank_name}</span></div>
                          <span className="font-bold text-green-800">{formatCurrency(vehicleOffers.find(o => o.offer_status === 'ACCEPTED').net_disbursal_amount)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Eligible Banks Table */}
                  {eligibleResults.length > 0 && (
                    <div className="p-4 border-b">
                      <p className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-1.5">
                        <CheckCircle className="h-4 w-4" /> Approved Banks ({eligibleResults.length})
                      </p>
                      <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full text-sm" data-testid="eligible-banks-table">
                          <thead>
                            <tr className="bg-green-50 text-green-800">
                              <th className="text-left p-3 font-medium">Bank</th>
                              <th className="text-center p-3 font-medium">Score</th>
                              <th className="text-right p-3 font-medium">Interest</th>
                              <th className="text-right p-3 font-medium">Max Loan</th>
                              <th className="text-right p-3 font-medium">EMI/mo</th>
                              <th className="text-right p-3 font-medium">Tenure</th>
                              <th className="text-right p-3 font-medium">Proc. Fee</th>
                              <th className="text-center p-3 font-medium">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {eligibleResults.map((result) => (
                              <tr key={result.bank_id} className="hover:bg-green-50/50 transition-colors">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-slate-400" />
                                    <div>
                                      <p className="font-medium">{result.bank_name}</p>
                                      <p className="text-[10px] text-gray-400">{result.bank_code}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-3 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                                    result.eligibility_score >= 80 ? 'bg-green-100 text-green-700' :
                                    result.eligibility_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-orange-100 text-orange-700'
                                  }`}>{result.eligibility_score}</span>
                                </td>
                                <td className="p-3 text-right font-semibold text-blue-700">
                                  {result.interest_rate ? `${result.interest_rate}%` : '-'}
                                </td>
                                <td className="p-3 text-right font-semibold">
                                  {result.max_loan_amount ? formatCurrency(result.max_loan_amount) : '-'}
                                </td>
                                <td className="p-3 text-right">
                                  <span className="font-bold text-slate-800">{result.emi_amount ? formatCurrency(result.emi_amount) : '-'}</span>
                                </td>
                                <td className="p-3 text-right text-gray-600">
                                  {result.tenure_months ? `${result.tenure_months} mo` : '-'}
                                </td>
                                <td className="p-3 text-right text-gray-600">
                                  {result.processing_fee ? formatCurrency(result.processing_fee) : '-'}
                                </td>
                                <td className="p-3 text-center">
                                  {!hasAppliedToBank(vehicle.vehicle_id, result.bank_id) ? (
                                    <Button size="sm" className="h-7 text-xs"
                                      onClick={() => handleApplyLoan(vehicle.vehicle_id, result.bank_id)}
                                      disabled={applying === `${vehicle.vehicle_id}-${result.bank_id}`}
                                      data-testid={`apply-btn-${result.bank_id}`}>
                                      {applying === `${vehicle.vehicle_id}-${result.bank_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-blue-600 font-medium">Applied</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {eligibleResults.some(r => r.warnings?.length > 0) && (
                        <div className="mt-2 text-xs text-amber-600">
                          {eligibleResults.filter(r => r.warnings?.length > 0).map(r => (
                            <p key={r.bank_id} className="flex items-start gap-1 mt-0.5">
                              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span><strong>{r.bank_name}:</strong> {r.warnings.join('; ')}</span>
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Rejected Banks */}
                  {rejectedResults.length > 0 && (
                    <div className="p-4">
                      <button 
                        onClick={() => toggleRejectExpand(vehicle.vehicle_id)}
                        className="w-full flex items-center justify-between text-sm font-semibold text-red-600 mb-2 hover:text-red-700"
                        data-testid="toggle-rejected-btn"
                      >
                        <span className="flex items-center gap-1.5">
                          <XCircle className="h-4 w-4" /> Rejected Banks ({rejectedResults.length})
                        </span>
                        {expandedRejects[vehicle.vehicle_id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {expandedRejects[vehicle.vehicle_id] && (
                        <div className="space-y-2" data-testid="rejected-banks-list">
                          {rejectedResults.map((result) => (
                            <div key={result.bank_id} className="flex items-start gap-3 p-3 rounded-lg bg-red-50/50 border border-red-100">
                              <XCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-sm">{result.bank_name} <span className="text-gray-400 text-xs">({result.bank_code})</span></p>
                                </div>
                                {result.rejection_reasons?.map((reason, i) => (
                                  <p key={i} className="text-xs text-red-600 mt-0.5">{reason}</p>
                                ))}
                              </div>
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-gray-400 hover:text-gray-600 flex-shrink-0"
                                onClick={() => setBankOffersModal({ open: true, vehicle, application: null })}>
                                Manual Offer
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Add Manual Offer */}
                  {results.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 border-t">
                      <Button variant="outline" size="sm" onClick={() => setBankOffersModal({ open: true, vehicle, application: null })}>
                        <Plus className="h-4 w-4 mr-2" /> Add Manual Bank Offer
                      </Button>
                      <p className="text-[10px] text-gray-400 mt-1">For banks that approved outside normal criteria</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
      
      <BankOffersModal
        isOpen={bankOffersModal.open}
        onClose={() => setBankOffersModal({ open: false, vehicle: null, application: null })}
        lead={lead}
        vehicle={bankOffersModal.vehicle}
        application={bankOffersModal.application}
        onUpdate={() => {
          setBankOffersModal({ open: false, vehicle: null, application: null });
          onUpdate();
        }}
      />
    </Dialog>
  );
};

export default LoanProcessingModal;
